import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { curatedModels } from '../../data/models';
import { CuratedModel, License, Modality } from '../../types/content';
import { formatReleased } from '../../utils/format';
import { SourceRef } from '../SourceRef';
type SortKey = 'model' | 'lab' | 'released' | 'context';
type LicenseFilter = 'all' | 'open' | 'proprietary';
type ModalityFilter = 'all' | Modality;
const LICENSE_STYLE: Record<License, string> = {
  open: 'border-live/40 text-live',
  'open-weights': 'border-human/40 text-human',
  proprietary: 'border-line text-muted'
};
const LICENSE_LABEL: Record<License, string> = {
  open: 'Open',
  'open-weights': 'Open weights',
  proprietary: 'Proprietary'
};
function contextTokens(c: string): number {
  const m = c.match(/([\d.]+)\s*([KM])/);
  if (!m) return -1;
  return Number(m[1]) * (m[2] === 'M' ? 1e6 : 1e3);
}
const sorters: Record<SortKey, (a: CuratedModel, b: CuratedModel) => number> = {
  model: (a, b) => a.model.localeCompare(b.model),
  lab: (a, b) => a.lab.localeCompare(b.lab),
  released: (a, b) => (a.released ?? '0000').localeCompare(b.released ?? '0000'),
  context: (a, b) => contextTokens(a.context) - contextTokens(b.context)
};
export function CuratedModelTable() {
  const labs = useMemo(() => Array.from(new Set(curatedModels.map(m => m.lab))).sort(), []);
  const [lab, setLab] = useState('all');
  const [license, setLicense] = useState<LicenseFilter>('all');
  const [modality, setModality] = useState<ModalityFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('released');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const rows = useMemo(() => {
    const list = curatedModels.filter(m => (lab === 'all' || m.lab === lab) && (license === 'all' || (license === 'open' ? m.license !== 'proprietary' : m.license === 'proprietary')) && (modality === 'all' || m.modality === modality));
    const s = [...list].sort(sorters[sortKey]);
    return dir === 'asc' ? s : s.reverse();
  }, [lab, license, modality, sortKey, dir]);
  const sortBy = (k: SortKey) => {
    if (k === sortKey) setDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setSortKey(k);
      setDir(k === 'released' || k === 'context' ? 'desc' : 'asc');
    }
  };
  const sortTh = (k: SortKey, label: string) => <th key={k} scope="col" aria-sort={sortKey === k ? dir === 'asc' ? 'ascending' : 'descending' : 'none'} className="whitespace-nowrap border-b border-line px-3 py-2.5 text-left font-normal" data-id="element-334" data-mp="15zu5b03rb58u">
      <button type="button" onClick={() => sortBy(k)} className={`inline-flex items-center gap-1 text-[12px] ${sortKey === k ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-335" data-mp="w8zhhrav3xla">
        {label}
        {sortKey === k ? dir === 'asc' ? <ArrowUpIcon className="h-3 w-3" data-id="element-336" data-mp="n7rljfvtj8ud" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-337" data-mp="navv6ss1ywsf" /> : <span className="w-3" data-id="element-338" data-mp="i96jdax0af3a" />}
      </button>
    </th>;
  return <div data-id="element-339" data-mp="ic2i31goh7ss">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pb-4" data-id="element-340" data-mp="2tx1xlxj2f7ld">
        <label className="flex items-center gap-2 text-[13px] text-muted" data-id="element-341" data-mp="2tz60cc58a0k2">
          Lab
          <select value={lab} onChange={e => setLab(e.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[13px] text-ink focus:border-machine focus:outline-none" data-id="element-342" data-mp="2yubkp6izf761">
            <option value="all" data-id="element-343" data-mp="37w1lh2yr7igh">All labs</option>
            {labs.map(l => <option key={l} value={l} data-id="element-344" data-mp="2b7vei9ed0dz1">
                {l}
              </option>)}
          </select>
        </label>
        <ChipGroup label="License" value={license} onChange={setLicense} options={[['all', 'All'], ['open', 'Open / weights'], ['proprietary', 'Proprietary']]} data-id="element-345" data-mp="2g3amwltllho2" />
        <ChipGroup label="Modality" value={modality} onChange={setModality} options={[['all', 'All'], ['text', 'Text'], ['multimodal', 'Multimodal'], ['code', 'Code']]} data-id="element-346" data-mp="2oys37p560b0t" />
        <span className="ml-auto font-mono text-[12px] text-muted" aria-live="polite" data-id="element-347" data-mp="1wh5vikunpukm">
          {rows.length} shown
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-line" data-id="element-348" data-mp="1ddc7d24shuxo">
        <table className="w-full min-w-[1100px] border-collapse text-[13.5px]" data-id="element-349" data-mp="18f0yatmbjogi">
          <thead className="bg-surface" data-id="element-350" data-mp="1n3dxs0km7pfr">
            <tr data-id="element-351" data-mp="1n4f6vzi2m1uu">
              {sortTh('model', 'Model')}
              {sortTh('lab', 'Lab')}
              {sortTh('released', 'Released')}
              <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-normal text-muted" data-id="element-352" data-mp="1w3aeng0rjk09">Parameters</th>
              {sortTh('context', 'Context')}
              <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-normal text-muted" data-id="element-353" data-mp="1r6tq4kpnj3mn">License</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-normal text-muted" data-id="element-354" data-mp="2123v9hbcjr4n">Modality</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-normal text-muted" data-id="element-355" data-mp="boiajq7wkfnl">Notable capability</th>
            </tr>
          </thead>
          <tbody data-id="element-356" data-mp="2ibplossgl0v3">
            {rows.map(m => <tr key={m.model} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-357" data-mp="2ie29vnot87gl">
                <td className="whitespace-nowrap px-3 py-2.5 text-ink" data-id="element-358" data-mp="2ncvgcw5oiy79">
                  {m.model}
                  <SourceRef href={m.sourceUrl} label={`${m.model} announcement`} data-id="element-359" data-mp="28kbpatvt0h66" />
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-muted" data-id="element-360" data-mp="1zlhmglrvquk6">{m.lab}</td>
                <td className={`whitespace-nowrap px-3 py-2.5 font-mono text-[12.5px] ${m.released ? 'text-ink' : 'text-faint'}`} data-id="element-361" data-mp="1znkne0ukm83o">{formatReleased(m.released)}</td>
                <td className={`whitespace-nowrap px-3 py-2.5 font-mono text-[12.5px] ${m.params.startsWith('undisclosed') ? 'text-faint' : 'text-ink'}`} data-id="element-362" data-mp="1polnp7rs9mst">{m.params}</td>
                <td className={`whitespace-nowrap px-3 py-2.5 font-mono text-[12.5px] ${m.context === 'undisclosed' ? 'text-faint' : 'text-ink'}`} data-id="element-363" data-mp="3k0tcs4hfb9go">{m.context}</td>
                <td className="whitespace-nowrap px-3 py-2.5" data-id="element-364" data-mp="3oybmvy76fuq9">
                  <span className={`rounded border px-1.5 py-0.5 font-mono text-[10.5px] ${LICENSE_STYLE[m.license]}`} title={m.licenseName} data-id="element-365" data-mp="3nkad7jwmizzu">
                    {LICENSE_LABEL[m.license]}
                  </span>
                  <span className="ml-2 text-[11.5px] text-faint" data-id="element-366" data-mp="3dll1k8vbmbs1">{m.licenseName}</span>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 capitalize text-muted" data-id="element-367" data-mp="38nt0ao8sddb6">{m.modality}</td>
                <td className="px-3 py-2.5 text-ink/85" data-id="element-368" data-mp="a3kpoimn9m95">{m.capability}</td>
              </tr>)}
            {rows.length === 0 && <tr data-id="element-369" data-mp="e16tcyxlsea">
                <td colSpan={8} className="px-3 py-10 text-center text-muted" data-id="element-370" data-mp="dhovw9a7409">
                  No models match these filters.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
}
interface ChipGroupProps<T extends string> {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: Array<[T, string]>;
}
function ChipGroup<T extends string>({
  label,
  value,
  onChange,
  options
}: ChipGroupProps<T>) {
  return <div role="radiogroup" aria-label={label} className="flex items-center gap-1.5" data-id="element-371" data-mp="10dtkj33fscmq">
      <span className="mr-1 text-[13px] text-muted" data-id="element-372" data-mp="15dpox3g1q62t">{label}</span>
      {options.map(([v, l]) => <button key={v} type="button" role="radio" aria-checked={v === value} onClick={() => onChange(v)} className={`whitespace-nowrap rounded-full border px-2.5 py-1 text-[12.5px] transition-colors duration-150 ${v === value ? 'border-machine bg-machine/10 text-ink' : 'border-line text-muted hover:text-ink'}`} data-id="element-373" data-mp="1ac9d7ai43tyc">
          {l}
        </button>)}
    </div>;
}